/**
 * Build Review — focused, shareable, single-page analysis of an arrow build.
 *
 * Wraps the v137 LivePreview surface (warnings + diagram + metrics + FOC +
 * spine + stability + trajectory + weight breakdown) in a standalone route
 * so testers and prospective dealers can land directly on the analysis view
 * without going through the full ArrowBuilder.
 *
 * Inputs:
 *   - Loads the active arrow from useSetups (the same store the Builder uses)
 *   - If a URL hash contains a serialized build state, that wins (so the
 *     scenario URLs we hand testers carry through here)
 *
 * Header is light/bone-themed because it ships on the Shopify cream
 * background — matches the Spine What-If and Wind-Drift pages. The
 * Build Review card section is dark-themed (matches the existing v137
 * MetricsCards/FOCBar styling).
 */

import { useEffect, useMemo, useState } from 'react';
import { useSetups } from '@/hooks/useSetups';
import { useBuildState, INITIAL_BUILD } from '@/hooks/useBuildState';
import { LivePreview } from '@/components/LivePreview';
import { WelcomeModal } from '@/components/WelcomeModal';
import type { BuildState, Setup } from '@/types';
import './build-review.css';

export function BuildReviewPage() {
  const setups = useSetups();
  const { activeSetup, activeArrow } = setups;

  /* Decode a build from the URL hash if present. Same base64url JSON
   * scheme the wind-drift visualizer uses for shareable scenarios. */
  const initialFromHash = useMemo(() => decodeBuildFromHash(), []);

  /* useBuildState is the v137 reducer that powers the Builder. We seed it
   * with: (1) the URL-hash build if present, (2) the active arrow from
   * useSetups, or (3) the default INITIAL_BUILD. Then a small effect keeps
   * it in sync if the user switches setups in the header. */
  const seed: BuildState =
    initialFromHash ?? activeArrow ?? INITIAL_BUILD;
  const build = useBuildState(seed);

  /* When the user picks a different setup from the dropdown, load the
   * corresponding arrow into the build state. */
  const [lastLoadedArrowKey, setLastLoadedArrowKey] = useState<string | null>(
    activeSetup ? `${activeSetup.id}:${activeSetup.activeArrowIdx}` : null,
  );
  useEffect(() => {
    if (initialFromHash) return; // hash wins; do not override
    if (!activeArrow || !activeSetup) return;
    const key = `${activeSetup.id}:${activeSetup.activeArrowIdx}`;
    if (key !== lastLoadedArrowKey) {
      build.load(activeArrow);
      setLastLoadedArrowKey(key);
    }
  }, [activeSetup, activeArrow, build, initialFromHash, lastLoadedArrowKey]);

  const buildLabel = useMemo(() => buildSummaryLabel(build.state, activeSetup), [build.state, activeSetup]);

  const allArrows = (activeSetup?.arrows ?? []).map((a, idx) => ({
    idx,
    label: a.name || `Arrow ${idx + 1}`,
  }));

  return (
    <div className="build-review-page">
      {/* First-visit blocking safety/legal acknowledgment — self-gates on the
          localStorage ack key, so it shows once per disclaimer version. Was
          missing on this deployed entry (only ArrowBuilder + wind-drift had it). */}
      <WelcomeModal />

      <header className="build-review-header">
        <nav className="forge-nav">
          <a href="/index.html">The Forge</a>
          <a href="https://sparrowexpeditions.com/pages/the-forge" target="_top">The Forge</a>
          <a href="/wind-drift.html">Wind-Drift</a>
          <a href="/build-review.html" className="active">Build Review</a>
          <a href="/tune.html">Tune Wizard</a>
          <a href="/range.html">Range Log</a>
          <a href="/bows.html">My Bows</a>
          <a href="/arrows.html">My Arrows</a>
          <a href="/spine.html">Spine</a>
        </nav>

        <div className="header-title-row">
          <div>
            <p className="stamp">Build Review · v140</p>
            <h1>{buildLabel}</h1>
            <p className="lede">
              Warnings, balance, FOC, spine, stability, and trajectory for the
              arrow on the right. Save this URL to share the same review.
            </p>
          </div>

          {allArrows.length > 1 && activeSetup && (
            <label className="arrow-picker">
              <span>Arrow</span>
              <select
                value={activeSetup.activeArrowIdx}
                onChange={(e) => setups.setActiveArrow(parseInt(e.target.value, 10))}
              >
                {allArrows.map((a) => (
                  <option key={a.idx} value={a.idx}>{a.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {initialFromHash && (
          <div className="hash-banner">
            Viewing a shared build (loaded from URL). Edits here won't save
            to your library —{' '}
            <a href="/index.html">open it in the Builder</a> to save.
          </div>
        )}
      </header>

      <main className="build-review-main">
        <LivePreview state={build.state} build={build} />
      </main>

      <footer className="build-review-footer">
        <p>
          Sources & math:{' '}
          <a href="/pages/methods#forgescore">Forge Score</a> ·{' '}
          <a href="/pages/methods#ashby">Ashby penetration</a> ·{' '}
          <a href="/blogs/field-notes/500-grain-eland-what-ashby-missed">Field Notes — Eland</a>
        </p>
      </footer>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── */

function buildSummaryLabel(s: BuildState, setup: Setup | null): string {
  const setupName = setup?.name ?? 'Active setup';
  const arrowName = s.name ?? 'Build';
  return `${setupName} · ${arrowName}`;
}

function decodeBuildFromHash(): BuildState | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  /* Hash format: #b=<base64url(json)> */
  const m = hash.match(/[#&]b=([^&]+)/);
  if (!m) return null;
  try {
    const b64 = m[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    const parsed = JSON.parse(json) as Partial<BuildState>;
    return { ...INITIAL_BUILD, ...parsed };
  } catch {
    return null;
  }
}
